import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core/';
import { Room } from '@material-ui/icons';
import React from 'react';
import { Text } from '../../components/DataDisplay';
import { RadioGroup } from '../../components/Inputs';
import {
  ComboField,
  CurrencyField,
  DatePickerField,
  PhoneField,
  SsnField,
  TextField,
} from '../../components/Inputs/Fields';
import { timeInMonthsCoSigner } from '../../utils/timeInMonths';
import States from '../../utils/us-states';
import { ZipCodeLookup } from '../ZipCodeLookup';
import { State } from '../ApplicationForm/types';
import Captcha from './Captcha';
import {
  DealApplicantInfo,
  EmploymentStatus,
  PropertyOwnership,
} from '../../types/applicants';

type Props = {
  state: DealApplicantInfo;
  captchaState: string | null;
  createRequestLoading: boolean;
  createRequestSuccess: boolean;
  setCaptchaState: React.Dispatch<React.SetStateAction<string | null>>;
  mainApplicantInfo: DealApplicantInfo;
  setState: React.Dispatch<React.SetStateAction<DealApplicantInfo>>;
};
export default function CoSignerForm({
  state,
  setCaptchaState,
  createRequestLoading,
  createRequestSuccess,
  captchaState,
  mainApplicantInfo,
  setState,
}: Props) {
  const [cities, setCities] = React.useState<{
    current: string[];
    previous: string[];
  }>({
    current: [],
    previous: [],
  });
  const [middleNameRequired, setMiddleNameRequired] = React.useState(true);
  return (
    <>
      <div
        style={{
          marginBottom: '10px',
          boxShadow: '0 3px 6px rgba(0,0,0,0.16)',
          borderRadius: '3px',
          padding: '15px',
        }}
      >
        <div>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12}>
              {' '}
              <Typography
                style={{
                  fontSize: '18px',
                  marginBottom: '10px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                Co-signer
              </Typography>{' '}
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                label="First Name"
                required={true}
                name={`applicantInfo.firstName`}
                schema="new_co_signer_request"
                onChange={(newValue) =>
                  setState({ ...state, firstName: newValue })
                }
                value={state.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Middle Name"
                required={middleNameRequired}
                disabled={!middleNameRequired}
                onChange={(newValue) =>
                  setState({ ...state, middleName: newValue })
                }
                name={`applicantInfo.middleName`}
                schema="new_co_signer_request"
                value={state.middleName ?? null}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                onChange={() => {
                  setMiddleNameRequired((prev) => !prev);
                }}
                control={
                  <Checkbox
                    id={'middleNameRequired'}
                    checked={!middleNameRequired}
                  />
                }
                label={
                  <Typography style={{ fontSize: '12px' }}>
                    I don't have a middle name
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                label="Last Name"
                required={true}
                name={`applicantInfo.lastName`}
                schema="new_co_signer_request"
                value={state.lastName}
                onChange={(newValue) =>
                  setState({ ...state, lastName: newValue })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="ID No."
                required={true}
                name={`applicantInfo.driverLicenseNumber`}
                schema="new_co_signer_request"
                value={state.driverLicenseNumber}
                onChange={(newValue) =>
                  setState({ ...state, driverLicenseNumber: newValue })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ComboField<State>
                labelProp="name"
                valueProp="abbreviation"
                label="ID Issued By"
                required={true}
                options={[...States]}
                name={`applicantInfo.drivingLicenseState`}
                schema="new_co_signer_request"
                value={state.drivingLicenseState}
                onChange={(newValue) =>
                  setState({ ...state, drivingLicenseState: newValue })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePickerField
                label="ID Issued Date"
                required={true}
                name={`applicantInfo.driverLicenseIssued`}
                schema="new_co_signer_request"
                value={state.driverLicenseIssued}
                onChange={(newValue) =>
                  setState({ ...state, driverLicenseIssued: newValue })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePickerField
                label="ID Exp. Date"
                required={true}
                name={`applicantInfo.driverLicenseExpires`}
                schema="new_co_signer_request"
                value={state.driverLicenseExpires}
                onChange={(newValue) =>
                  setState({ ...state, driverLicenseExpires: newValue })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePickerField
                label="Date of Birth"
                required={true}
                type="birth-date"
                name={`applicantInfo.birthDate`}
                schema="new_co_signer_request"
                value={state.birthDate ?? null}
                onChange={(newValue) =>
                  setState({ ...state, birthDate: newValue })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SsnField
                label="Soc. Sec. No."
                required={true}
                name={`applicantInfo.socialSecurityNumber`}
                schema="new_co_signer_request"
                value={state.socialSecurityNumber ?? null}
                onChange={(newValue) =>
                  setState({ ...state, socialSecurityNumber: newValue })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PhoneField
                label="Primary Phone"
                required={true}
                name={`applicantInfo.mobilePhone`}
                schema="new_co_signer_request"
                value={state.mobilePhone}
                onChange={(newValue) =>
                  setState({ ...state, mobilePhone: newValue })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PhoneField
                label="Secondary Phone"
                name={`applicantInfo.homePhone`}
                schema="new_co_signer_request"
                value={state.homePhone}
                onChange={(newValue) =>
                  setState({ ...state, homePhone: newValue })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email Address"
                required={true}
                name={`applicantInfo.email`}
                schema="new_co_signer_request"
                value={state.email}
                onChange={(newValue) => setState({ ...state, email: newValue })}
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RadioGroup
                radioOptions={[
                  {
                    value: 'Yes',
                    label: 'Yes',
                  },
                  {
                    value: 'No',
                    label: 'No',
                  },
                ]}
                row={true}
                label="Is US citizen?"
                required={true}
                name={`applicantInfo.usCitizen`}
                value={state.usCitizen ?? 'Yes'}
                onChange={(newValue) =>
                  setState({ ...state, usCitizen: newValue as 'Yes' | 'No' })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RadioGroup
                radioOptions={[
                  {
                    value: 'Married',
                    label: 'Married',
                  },
                  {
                    value: 'Not married',
                    label: 'Not married',
                  },
                ]}
                row={true}
                label="Marital status"
                required={true}
                name={`applicantInfo.maritalStatus`}
                value={state.maritalStatus ?? 'Yes'}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    maritalStatus:
                      (newValue as 'Married' | 'Not married' | null) ??
                      'Married',
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Text fontSize={16} fontStyle="italic" text="Current address" />
            </Grid>

            <Grid item xs={12} style={{ margin: '2px' }}>
              <Button
                onClick={() => {
                  setState({
                    ...state,
                    currentAddressNumber:
                      mainApplicantInfo?.currentAddressNumber,
                    currentAddress: mainApplicantInfo?.currentAddress,
                    currentCity: mainApplicantInfo?.currentCity,
                    currentState: mainApplicantInfo?.currentState,
                    currentZipCode: mainApplicantInfo?.currentZipCode,
                    yearsAtCurrentAddress:
                      mainApplicantInfo?.yearsAtCurrentAddress,
                    monthsAtCurrentAddress:
                      mainApplicantInfo?.monthsAtCurrentAddress,
                    currentApt: null,
                  });
                }}
                color="primary"
                size="small"
                variant="contained"
              >
                Same as the main applicant
              </Button>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                label="House Num."
                required={true}
                type="number"
                name={`applicantInfo.currentAddressNumber`}
                schema="new_co_signer_request"
                value={state.currentAddressNumber}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    currentAddressNumber: newValue,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Street Name"
                required={true}
                name={`applicantInfo.currentAddress`}
                schema="new_co_signer_request"
                value={state.currentAddress}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    currentAddress: newValue,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Apt/Unit"
                name={`applicantInfo.currentApt`}
                schema="new_co_signer_request"
                value={state.currentApt}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    currentApt: newValue,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Zip"
                required={true}
                name={`applicantInfo.currentZipCode`}
                schema="new_co_signer_request"
                value={state.currentZipCode}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    currentZipCode: newValue,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <ZipCodeLookup
                addressType="current"
                setCities={setCities}
                onChange={(city, usState) =>
                  setState({
                    ...state,
                    currentCity: city,
                    currentState: usState as (typeof States)[number]['name'],
                  })
                }
                zipCode={state.currentZipCode}
                cityPath={`applicantInfo.currentCity`}
                statePath={`applicantInfo.currentState`}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="City"
                required={true}
                schema="new_co_signer_request"
                name={`applicantInfo.currentCity`}
                value={state.currentCity}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    currentCity: newValue,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ComboField<State>
                labelProp="name"
                valueProp="name"
                label="State"
                required={true}
                options={[...States]}
                schema="new_co_signer_request"
                name={`applicantInfo.currentState`}
                value={state.currentState}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    currentState: newValue as (typeof States)[number]['name'],
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <List component="nav" aria-label="main mailbox folders">
                {cities.current.length > 1 &&
                  cities.current.map((city, index) => (
                    <div key={index}>
                      <ListItem
                        button
                        onClick={() => {
                          setState({
                            ...state,
                            currentCity: city,
                          });
                        }}
                      >
                        <ListItemIcon>
                          <Room />
                        </ListItemIcon>
                        <ListItemText>{city}</ListItemText>
                      </ListItem>
                      <Divider />
                    </div>
                  ))}
              </List>
            </Grid>

            <Grid item xs={12}>
              <Text
                fontSize={16}
                fontStyle="italic"
                text="Time at current address"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Years"
                required={true}
                schema="new_co_signer_request"
                name={`applicantInfo.yearsAtCurrentAddress`}
                value={state.yearsAtCurrentAddress}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    yearsAtCurrentAddress: newValue ? parseInt(newValue) : null,
                  })
                }
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Months"
                schema="new_co_signer_request"
                name={`applicantInfo.monthsAtCurrentAddress`}
                value={state.monthsAtCurrentAddress}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    monthsAtCurrentAddress: newValue
                      ? parseInt(newValue)
                      : null,
                  })
                }
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <RadioGroup
                radioOptions={[
                  {
                    value: 'Owner',
                    label: 'Own',
                  },
                  {
                    value: 'Rent',
                    label: 'Rent',
                  },
                  {
                    value: 'Family',
                    label: 'Family',
                  },
                  {
                    value: 'Other',
                    label: 'Other',
                  },
                ]}
                row={true}
                label="Residential Status"
                required={true}
                name={`propertyOwnership`}
                value={state.propertyOwnership ?? 'Owner'}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    propertyOwnership: newValue as PropertyOwnership,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CurrencyField
                label="Rent or Mortgage Amount"
                required={true}
                schema="new_co_signer_request"
                name={`applicantInfo.${
                  state?.propertyOwnership === 'Rent'
                    ? 'rentPerMonth'
                    : 'mortgagePerMonth'
                }`}
                value={state.rentPerMonth}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    [state?.propertyOwnership === 'Rent'
                      ? 'rentPerMonth'
                      : 'mortgagePerMonth']: newValue,
                  })
                }
              />
            </Grid>
            {timeInMonthsCoSigner(
              state.yearsAtCurrentAddress || 0,
              state.monthsAtCurrentAddress || 0
            ) < 24 && (
              <>
                <Grid item xs={12}>
                  <Text
                    fontSize={16}
                    fontStyle="italic"
                    text="Previous address"
                  />
                </Grid>

                <Grid item xs={12} sm={12} style={{ margin: '2px' }}>
                  <Button
                    onClick={() => {
                      setState({
                        ...state,
                        previousAddressNumber:
                          mainApplicantInfo?.previousAddressNumber,
                        previousAddress: mainApplicantInfo?.previousAddress,
                        previousCity: mainApplicantInfo?.previousCity,
                        previousState:
                          mainApplicantInfo?.previousState as (typeof States)[number]['name'],
                        previousZipCode: mainApplicantInfo?.previousZipCode,
                        yearsAtPreviousAddress:
                          mainApplicantInfo?.yearsAtPreviousAddress,
                        monthsAtPreviousAddress:
                          mainApplicantInfo?.monthsAtPreviousAddress,
                        previousApt: null,
                      });
                    }}
                    color="primary"
                    size="small"
                    variant="contained"
                  >
                    Same as the main applicant
                  </Button>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    label="House Num."
                    required={true}
                    type="number"
                    schema="new_co_signer_request"
                    name={`applicantInfo.previousAddressNumber`}
                    value={state.previousAddressNumber}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        previousAddressNumber: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Full Street Name"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.previousAddress`}
                    value={state.previousAddress}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        previousAddress: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Apt/Unit"
                    schema="new_co_signer_request"
                    name={`applicantInfo.previousApt`}
                    value={state.previousApt}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        previousApt: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Zip"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.previousZipCode`}
                    value={state.previousZipCode}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        previousZipCode: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <ZipCodeLookup
                    addressType="previous"
                    zipCode={state.previousZipCode}
                    setCities={setCities}
                    onChange={(city, usState) =>
                      setState({
                        ...state,
                        previousCity: city,
                        previousState:
                          usState as (typeof States)[number]['name'],
                      })
                    }
                    cityPath={`applicantInfo.previousCity`}
                    statePath={`applicantInfo.previousState`}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="City"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.previousCity`}
                    value={state.previousCity}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        previousCity: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ComboField<State>
                    labelProp="name"
                    valueProp="name"
                    label="State"
                    required={true}
                    options={[...States]}
                    schema="new_co_signer_request"
                    name={`applicantInfo.previousState`}
                    value={state.previousState}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        previousState:
                          newValue as (typeof States)[number]['name'],
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <List component="nav" aria-label="main mailbox folders">
                    {cities.previous.length > 1 &&
                      cities.previous.map((city, index) => (
                        <div key={index}>
                          <ListItem
                            button
                            onClick={() => {
                              setState({
                                ...state,
                                previousCity: city,
                              });
                            }}
                          >
                            <ListItemIcon>
                              <Room />
                            </ListItemIcon>
                            <ListItemText>{city}</ListItemText>
                          </ListItem>
                          <Divider />
                        </div>
                      ))}
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Text
                    fontSize={16}
                    fontStyle="italic"
                    text="Time at previous address"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Years"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.yearsAtPreviousAddress`}
                    value={state.yearsAtPreviousAddress}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        yearsAtPreviousAddress: newValue
                          ? parseInt(newValue)
                          : null,
                      })
                    }
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Months"
                    schema="new_co_signer_request"
                    name={`applicantInfo.monthsAtPreviousAddress`}
                    value={state.monthsAtPreviousAddress}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        monthsAtPreviousAddress: newValue
                          ? parseInt(newValue)
                          : null,
                      })
                    }
                    type="number"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <RadioGroup
                radioOptions={[
                  {
                    value: 'Employee',
                    label: 'Employee',
                  },
                  {
                    value: 'Business owner',
                    label: 'Business owner',
                  },
                  {
                    value: 'Retired',
                    label: 'Retired',
                  },
                ]}
                row={true}
                label="Employment status"
                required={true}
                name={`applicantInfo.employmentStatus`}
                value={state.employmentStatus ?? 'Employee'}
                onChange={(newValue) =>
                  setState({
                    ...state,
                    employmentStatus: newValue as EmploymentStatus,
                  })
                }
              />
            </Grid>
            {state.employmentStatus !== 'Retired' && (
              <React.Fragment>
                <Grid item xs={12}>
                  <Text
                    fontSize={16}
                    fontStyle="italic"
                    text="Current employer"
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Name"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.employer`}
                    value={state.employer}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        employer: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <CurrencyField
                    label="Gross Monthly Salary"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.monthlyIncome`}
                    value={state.monthlyIncome}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        monthlyIncome: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneField
                    label="Work Phone"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.businessPhone`}
                    value={state.businessPhone}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        businessPhone: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Occupation/Job Title"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.jobOccupation`}
                    value={state.jobOccupation}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        jobOccupation: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Text
                    fontSize={16}
                    fontStyle="italic"
                    text="Time at current employer"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Years"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.yearsAtCurrentJob`}
                    value={state.yearsAtCurrentJob}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        yearsAtCurrentJob: newValue ? parseInt(newValue) : null,
                      })
                    }
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Months"
                    schema="new_co_signer_request"
                    name={`applicantInfo.monthsAtCurrentJob`}
                    value={state.monthsAtCurrentJob}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        monthsAtCurrentJob: newValue
                          ? parseInt(newValue)
                          : null,
                      })
                    }
                    type="number"
                  />
                </Grid>
              </React.Fragment>
            )}
            {state.employmentStatus === 'Retired' && (
              <>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Text
                      fontSize={16}
                      fontStyle="italic"
                      text="Retirement income"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      label="Retirement Income source"
                      schema="new_co_signer_request"
                      name={`applicantInfo.sourceOfRetirementIncome`}
                      value={state.sourceOfRetirementIncome}
                      onChange={(newValue) =>
                        setState({
                          ...state,
                          sourceOfRetirementIncome: newValue,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CurrencyField
                      required
                      label="Retirement income"
                      schema="new_co_signer_request"
                      name={`applicantInfo.retirementIncome`}
                      value={state.retirementIncome}
                      onChange={(newValue) =>
                        setState({ ...state, retirementIncome: newValue })
                      }
                    />
                  </Grid>
                </Grid>
              </>
            )}
            {state.employmentStatus === 'Retired' ||
            timeInMonthsCoSigner(
              state.yearsAtCurrentJob || 0,
              state.monthsAtCurrentJob || 0
            ) < 24 ? (
              <>
                <Grid item xs={12}>
                  <Text
                    fontSize={16}
                    fontStyle="italic"
                    text="Previous employer"
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    label="Name"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.previousEmployer`}
                    value={state.previousEmployer}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        previousEmployer: newValue,
                      })
                    }
                  />
                </Grid>
                {state.employmentStatus === 'Retired' && (
                  <Grid item xs={12} sm={5}>
                    <CurrencyField
                      label="Gross Monthly Salary"
                      required={true}
                      schema="new_co_signer_request"
                      name={`applicantInfo.monthlyIncome`}
                      value={state.monthlyIncome}
                      onChange={(newValue) =>
                        setState({
                          ...state,
                          monthlyIncome: newValue,
                        })
                      }
                    />
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sm={state.employmentStatus === 'Retired' ? 12 : 5}
                >
                  <TextField
                    label="Occupation/Job Title"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.previousOccupation`}
                    value={state.previousOccupation}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        previousOccupation: newValue,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Text
                    fontSize={16}
                    fontStyle="italic"
                    text="Time at previous employer"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Years"
                    required={true}
                    schema="new_co_signer_request"
                    name={`applicantInfo.yearsAtPreviousJob`}
                    value={state.yearsAtPreviousJob}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        yearsAtPreviousJob: newValue
                          ? parseInt(newValue)
                          : null,
                      })
                    }
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Months"
                    schema="new_co_signer_request"
                    name={`applicantInfo.prevEmployer.monthsAtPreviousJob`}
                    value={state.monthsAtPreviousJob}
                    onChange={(newValue) =>
                      setState({
                        ...state,
                        monthsAtPreviousJob: newValue
                          ? parseInt(newValue)
                          : null,
                      })
                    }
                    type="number"
                  />
                </Grid>
              </>
            ) : null}
          </Grid>
        </div>
      </div>
      <div
        style={{
          marginBottom: '10px',
          boxShadow: '0 3px 6px rgba(0,0,0,0.16)',
          borderRadius: '3px',
          padding: '10px 15px 15px 15px',
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <CurrencyField
              label="Gross Monthly Other Income"
              schema="new_co_signer_request"
              name={`applicantInfo.additionalIncomes.value`}
              value={state.additionalIncomes?.[0]?.value || 0}
              onChange={(newValue) =>
                setState({
                  ...state,
                  additionalIncomes: [
                    {
                      value: newValue || 0,
                      source: state?.additionalIncomes?.[0]?.source,
                    },
                  ],
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Other income source"
              schema="new_co_signer_request"
              name={`applicantInfo.additionalIncomes.source`}
              value={state.additionalIncomes?.[0]?.source}
              onChange={(newValue) =>
                setState({
                  ...state,
                  additionalIncomes: [
                    {
                      value: state?.additionalIncomes?.[0]?.value,
                      source: newValue ?? undefined,
                    },
                  ],
                })
              }
            />
          </Grid>
          <Grid item container xs={12}>
            <Grid item xs={8}>
              <Captcha handleChange={(token) => setCaptchaState(token)} />
            </Grid>
            <Grid
              item
              xs={4}
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                style={{ float: 'right' }}
                startIcon={
                  createRequestLoading ? (
                    <CircularProgress size={20} style={{ color: '#ffffff' }} />
                  ) : undefined
                }
                color="primary"
                variant="contained"
                disabled={!captchaState}
                type={'submit'}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
          {createRequestSuccess && (
            <Grid item xs={12}>
              <Typography style={{ color: '#03920C' }}>
                Your co-signer request has been submitted successfully!
              </Typography>
            </Grid>
          )}
        </Grid>
      </div>
    </>
  );
}
